// react
import React, { Component } from 'react'
import PropTypes from 'prop-types'

// components
import Calendar from './Calendar.jsx'
import { Icon } from '@st-operations/uikit/src/v2/index.js'

// css
import './MonthPicker.scss'

// constants
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// component
class MonthPicker extends Component {
  constructor(props) {
    super(props)

    this.wrapperRef = React.createRef()
    this.calendarRef = React.createRef()
    this.state = {
      currentYear: null,
      years: [],
      isCalendarOpen: false,
      selectionPhase: 'start', // 'start' | 'end'
      pendingStart: null,
      hoverMonthNum: null,
      bottom: 0,
      left: 0,
    }
  }

  componentDidMount() {
    const { from, to, beginByLastYear } = this.props
    const firstYear = Number(from.split('-')[0])
    const finalYear = Number(to.split('-')[0])
    const years = [...Array(finalYear - firstYear + 1).keys()].map((_, i) => i + firstYear)

    document.addEventListener('mousedown', this.handleClickOutside)
    this.setState({
      currentYear: beginByLastYear ? years[years.length - 1] : years[0],
      years,
    })
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (event) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target) &&
      this.calendarRef.current &&
      !this.calendarRef.current.contains(event.target)
    ) {
      this.setState({ isCalendarOpen: false, selectionPhase: 'start', pendingStart: null, hoverMonthNum: null })
    }
  }

  handleOpenCalendar = () => {
    const { bottom, left } = this.wrapperRef.current.getBoundingClientRect()

    this.setState({
      bottom: window.innerHeight - bottom + (this.props.popupDirectionBottom ? -300 : 56),
      isCalendarOpen: true,
      left,
      selectionPhase: 'start',
      pendingStart: null,
      hoverMonthNum: null,
    })
  }

  handleCloseCalendar = () => {
    this.setState({ isCalendarOpen: false, selectionPhase: 'start', pendingStart: null, hoverMonthNum: null })
  }

  handleNextYear = () => {
    this.setState((prev) => ({ currentYear: prev.currentYear + 1 }))
  }

  handlePreviousYear = () => {
    this.setState((prev) => ({ currentYear: prev.currentYear - 1 }))
  }

  toMonthNum = (dateStr) => {
    if (!dateStr) return null
    const [year, month] = dateStr.split('-')
    return Number(year) * 12 + Number(month)
  }

  formatDate = (dateStr) => {
    if (!dateStr) return ''
    const [year, month] = dateStr.split('-')
    return `${month}/${year}`
  }

  getInputDisplay = () => {
    const { startValue, endValue } = this.props
    const { selectionPhase, pendingStart } = this.state

    if (selectionPhase === 'end' && pendingStart) {
      return `${this.formatDate(pendingStart)} → ...`
    }
    if (startValue && endValue) {
      return `${this.formatDate(startValue)} → ${this.formatDate(endValue)}`
    }
    return ''
  }

  handleSelectMonth = (event) => {
    const { currentYear, selectionPhase, pendingStart } = this.state
    const { onChange } = this.props
    const monthIndex = Number(event.currentTarget.value)
    const dateStr = `${currentYear}-${('0' + (monthIndex + 1)).slice(-2)}-01`

    if (selectionPhase === 'start') {
      this.setState({ selectionPhase: 'end', pendingStart: dateStr, hoverMonthNum: null })
    } else {
      if (dateStr >= pendingStart) {
        onChange(pendingStart, dateStr)
        this.handleCloseCalendar()
      } else {
        // Clicked before pending start — restart with this as the new start
        this.setState({ pendingStart: dateStr, hoverMonthNum: null })
      }
    }
  }

  handleMonthHover = (year, monthIndex) => {
    this.setState({ hoverMonthNum: year * 12 + (monthIndex + 1) })
  }

  handleMonthHoverEnd = () => {
    this.setState({ hoverMonthNum: null })
  }

  isMonthDisabled = (index) => {
    const { from, to } = this.props
    const { currentYear, years } = this.state

    const [lastYear, lastMonth] = to.split('-')
    const [firstYear, firstMonth] = from.split('-')
    const isFirstYear = Number(firstYear) === currentYear
    const isLastYear = Number(lastYear) === currentYear
    const hasMultipleYears = years.length > 1

    if (hasMultipleYears) {
      if (isFirstYear) return index < Number(firstMonth) - 1
      if (isLastYear) return index > Number(lastMonth) - 1
      return false
    }

    return index < Number(firstMonth) - 1 || index > Number(lastMonth) - 1
  }

  renderMonthButton = (month, index) => {
    const { currentYear, selectionPhase, pendingStart, hoverMonthNum } = this.state
    const { startValue, endValue } = this.props

    const effectiveStartNum =
      selectionPhase === 'end' ? this.toMonthNum(pendingStart) : this.toMonthNum(startValue)
    const effectiveEndNum =
      selectionPhase === 'end'
        ? hoverMonthNum && hoverMonthNum >= effectiveStartNum
          ? hoverMonthNum
          : null
        : this.toMonthNum(endValue)

    const btnMonthNum = currentYear * 12 + (index + 1)
    const isStart = btnMonthNum === effectiveStartNum
    const isEnd = btnMonthNum === effectiveEndNum
    const isInRange =
      effectiveStartNum &&
      effectiveEndNum &&
      btnMonthNum > effectiveStartNum &&
      btnMonthNum < effectiveEndNum

    let className = 'month-picker__month'
    if (isStart) className += ' range-start'
    if (isEnd) className += ' range-end'
    if (isInRange) className += ' in-range'

    return (
      <button
        className={className}
        disabled={this.isMonthDisabled(index)}
        key={`${currentYear}-${month}`}
        onClick={this.handleSelectMonth}
        onMouseEnter={() => this.handleMonthHover(currentYear, index)}
        onMouseLeave={this.handleMonthHoverEnd}
        value={index}
      >
        {month}
      </button>
    )
  }

  renderCalendar = () => {
    const { currentYear, years, bottom, left, selectionPhase } = this.state
    const { from, to } = this.props

    const [lastYear] = to.split('-')
    const [firstYear] = from.split('-')
    const isFirstYear = Number(firstYear) === currentYear
    const isLastYear = Number(lastYear) === currentYear
    const hasMultipleYears = years.length > 1
    const selectionLabel = selectionPhase === 'start' ? 'Select start month' : 'Select end month'

    return (
      <Calendar
        bottom={bottom}
        currentYear={currentYear}
        hasArrows={hasMultipleYears}
        isFirstYear={isFirstYear}
        isLastYear={isLastYear}
        left={left}
        onNextYear={this.handleNextYear}
        onPreviousYear={this.handlePreviousYear}
        ref={this.calendarRef}
        selectionLabel={selectionLabel}
      >
        {MONTHS.map((month, index) => this.renderMonthButton(month, index))}
      </Calendar>
    )
  }

  render() {
    const { isCalendarOpen } = this.state

    return (
      <div className="month-picker" ref={this.wrapperRef}>
        <div className="month-picker__input-container" onClick={this.handleOpenCalendar}>
          <input
            className="month-picker__input"
            readOnly
            type="text"
            value={this.getInputDisplay()}
          />
          <div className="month-picker__input-icon-container">
            <Icon className="month-picker__input-icon" height="24px" icon="calendar" width="24px" />
          </div>
        </div>
        {isCalendarOpen && this.renderCalendar()}
      </div>
    )
  }
}

MonthPicker.propTypes = {
  // Bounds (yyyy-mm-dd format)
  from: PropTypes.string,
  to: PropTypes.string,
  // Selected range values (yyyy-mm-dd format)
  startValue: PropTypes.string,
  endValue: PropTypes.string,
  // Called with (startDate, endDate) when a full range is selected
  onChange: PropTypes.func,
  beginByLastYear: PropTypes.bool,
  popupDirectionBottom: PropTypes.bool,
}

MonthPicker.defaultProps = {}

// export
export default MonthPicker
